import type { Track } from "@similarweb/tracking-provider";
import type { CustomAttributes } from "app/services/track/track";

import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { RequestAdminFeatureBase } from "app/pages/sales-intelligence/types/requestAdminFeature";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";

import type { ApiPageType } from "../../types/api-page-type";

type WebhookFeature = "signalsWebhook" | "findNewCompaniesWebhook";
type GatewayFeature = ApiPageType | RequestAdminFeatureBase;

const API_LABEL: Record<ApiPageType, string> = {
    technologies: "Technologies API",
    leadEnrichment: "Lead Enrichment API",
    contacts: "Contacts API",
    aiOutreach: "AI Outreach API",
    signalsRestApi: "Signals REST API",
};

const WEBHOOK_LABEL: Record<WebhookFeature, string> = {
    signalsWebhook: "Signals webhook",
    findNewCompaniesWebhook: "Find new companies",
};

const GATEWAY_FEATURE_LABEL: Record<GatewayFeature, string> = {
    ...API_LABEL,
    salesforce: "Salesforce",
    hubspot: "Hubspot",
    dynamics: "Dynamics",
    signalsWebhook: "Signals webhook",
    findNewCompaniesWebhook: "Find new companies",
};

export const createManageIntegrationsTrackingService = (track: Track) => {
    const trackCrmEvent = (integration: Integration, eventName: string) =>
        track(`${integration} integration`, "click", createTrackingEventName(eventName));

    const trackMcpConnectionEvent = (eventName: "connect" | "upgrade", toolName: string) =>
        track("MCP connection", "click", createTrackingEventName(eventName, toolName));

    const trackGatewayEvent = (eventName: string, feature: GatewayFeature) =>
        track("gateway page", "click", createTrackingEventName(eventName, GATEWAY_FEATURE_LABEL[feature]));

    const trackWebhookEvent = (webhook: WebhookFeature, customAttributes?: CustomAttributes) =>
        track(
            "webhook connection",
            "click",
            createTrackingEventName("webhook card", WEBHOOK_LABEL[webhook]),
            customAttributes,
        );

    const trackWebhookGuideEvent = (webhook: WebhookFeature) =>
        track("webhook guide", "click", createTrackingEventName("guide", GATEWAY_FEATURE_LABEL[webhook]));

    return {
        trackCrmConnect: (integration: Integration) => trackCrmEvent(integration, "connect"),
        trackCrmManage: (integration: Integration) => trackCrmEvent(integration, "manage"),
        trackCrmApproveTerms: (integration: Integration) => trackCrmEvent(integration, "approve T&C"),
        trackCrmGuideClick: (integration: Integration) => trackCrmEvent(integration, "guide"),
        trackDynamicsEnrichmentInterested: () =>
            track("dynamics enrichment", "click", createTrackingEventName("I'm interested")),

        trackApiCardClick: (api: ApiPageType) =>
            track("API connection", "click", createTrackingEventName("API card", API_LABEL[api])),
        trackApiGuideClick: (api: ApiPageType) =>
            track("API guide", "click", createTrackingEventName("guide", API_LABEL[api])),
        trackApiUpgradeClick: (api: ApiPageType) =>
            track("API card", "click", createTrackingEventName("upgrade", API_LABEL[api])),

        trackSignalsWebhookClick: (customAttributes?: CustomAttributes) =>
            trackWebhookEvent("signalsWebhook", customAttributes),
        trackFindNewCompaniesWebhookClick: () => trackWebhookEvent("findNewCompaniesWebhook"),
        trackSignalsWebhookGuideClick: () => trackWebhookGuideEvent("signalsWebhook"),
        trackFindNewCompaniesWebhookGuideClick: () => trackWebhookGuideEvent("findNewCompaniesWebhook"),

        trackMcpConnect: (toolName: string) => trackMcpConnectionEvent("connect", toolName),
        trackMcpUpgrade: (toolName: string) => trackMcpConnectionEvent("upgrade", toolName),
        trackMcpUrlCopy: () => track("MCP URL", "click", createTrackingEventName("copy")),
        trackMcpGuideClick: (toolName: string) =>
            track("MCP guide", "click", createTrackingEventName("guide", toolName)),

        trackConnectorGuideClick: (connectorName: string) =>
            track("connector guide", "click", createTrackingEventName("guide", connectorName)),

        trackGatewayApiKeyClick: (feature: GatewayFeature) => trackGatewayEvent("API key", feature),
        trackGatewayLearnMore: (feature: GatewayFeature) => trackGatewayEvent("learn more", feature),
        trackGatewayConnect: (feature: GatewayFeature) => trackGatewayEvent("connect", feature),
    };
};
